import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { LeftArrowWhite } from 'src/assets/images/svg/LeftArrow';
import { Button } from 'src/components/ui/Button';
import { applicationStore } from 'src/store/ApplicationStore';
import LocationImage from "../../assets/images/LocationImage.png"
import logger from 'src/utils/logger';
import { toast } from 'react-toastify';
import { workflowStepIterator } from 'src/services/workflowStepIterator';
import { NodeTypes } from 'src/enums/NodeTypes';
import { parseServiceErrorMessage } from 'src/helpers/parseServiceError';
import Rotator from 'src/components/animation/Rotator';
import { useTranslation } from 'react-i18next';

const LocationVerification = observer(() => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [locationStatus, setLocationStatus] = useState("checking"); // "checking", "success", "error"
    const [position, setPosition] = useState(null);

    const handleLocationSuccess = useCallback(async (position) => {
        logger.log("Location permission granted:", position.coords.latitude, position.coords.longitude);
        setPosition(position);
        setLocationStatus("success");
    }, []);

    const handleLocationError = useCallback((error) => {
        logger.error("Location permission denied:", error);
        setLocationStatus("error");
        applicationStore.setWorkflowNextServiceResponse({
            ...applicationStore.workflowNextServiceResponse,
            inprogress_services: []
        });
        applicationStore.setApplicationErrorObj({
            header: t('locationVerification.permissionErrorHeader'),
            description: t('locationVerification.permissionErrorDescription'),
            listItems: [
                {
                    baseDescription: t('locationVerification.permissionErrorReasonsTitle'),
                    reason: [
                        t('locationVerification.permissionErrorReasonVpnProxy'),
                        t('locationVerification.permissionErrorReasonBrowserPermission'),
                        t('locationVerification.permissionErrorReasonDeviceServices'),
                        t('locationVerification.permissionErrorReasonNetworkSecurity')
                    ].join(',')
                },
                {
                    baseDescription: t('locationVerification.permissionErrorActionsTitle'),
                    reason: [
                        t('locationVerification.permissionErrorActionDisableVpnProxy'),
                        t('locationVerification.permissionErrorActionAllowBrowserPermission'),
                        t('locationVerification.permissionErrorActionEnableDeviceServices'),
                        t('locationVerification.permissionErrorActionTryDifferentEnvironment')
                    ].join(',')
                }
            ],
            buttonList: [
                {
                    label: t('actions.retry'),
                    link: `/next`,
                    variant: "primaryWithoutIcon"
                },
                {
                    label: t('verification.continueAnotherDevice'),
                    variant: "secondaryWithoutIcon",
                    onClick: () => {
                        applicationStore.setContinueOnAnotherDevice("/next");
                        return navigate("/continue-on-another-device", { replace: true });
                    }
                }
            ]
        });
        navigate("/error-page", { replace: true });
    }, [navigate, t]);

    const requestLocationPermission = useCallback(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                handleLocationSuccess,
                handleLocationError,
                { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
            );
        } else {
            handleLocationError({ code: 0, message: t('locationVerification.browserNotSupported') });
        }
    }, [handleLocationSuccess, handleLocationError, t]);

    useEffect(() => {
        requestLocationPermission();
    }, [requestLocationPermission]);

    useEffect(() => {
        const processLocation = async () => {
            if (locationStatus === "success" && position) {
                try {
                    const response = await workflowStepIterator({
                        access_token: applicationStore.access_token,
                        node_id: applicationStore.workflowNextServiceResponse.node_id,
                        body: {
                            "lng": position.coords.longitude,
                            "lat": position.coords.latitude
                        },
                        action_index: 0,
                        constextOfStep: {
                            step: NodeTypes.GEOLOCATION_VERIFICATON,
                            retry: applicationStore.workflowNextServiceResponse.retry
                        }
                    });

                    if (response.data?.service_results[0].status !== 200) {
                        const rawMessage = response.data?.service_results[0]?.message;
                        const parsedMessage = parseServiceErrorMessage(rawMessage);
                        throw new Error(parsedMessage);
                    }
                    applicationStore.setWorkflowNextServiceResponse(response.data);
                    console.log(response.data?.service_results[0]?.data);
                    applicationStore.setAddressData({
                        city: response.data?.service_results[0]?.data?.city,
                        country: response.data?.service_results[0]?.data?.country,
                        addressLine1: response.data?.service_results[0]?.data?.address_line,
                        postcode: response.data?.service_results[0]?.data?.postcode,
                    });
                    navigate("/address-confirmation", { replace: true });

                } catch (error) {
                    toast.error(error?.message || t('locationVerification.couldNotGetLocation'));
                    setLocationStatus("error");
                    applicationStore.setWorkflowNextServiceResponse({
                        ...applicationStore.workflowNextServiceResponse,
                        inprogress_services: []
                    });
                    applicationStore.setApplicationErrorObj({
                        header: t('locationVerification.failed'),
                        description: t('locationVerification.failedDescription'),
                        buttonList: [
                            {
                                label: t('actions.retry'),
                                link: `/next`,
                                variant: "primaryWithoutIcon"
                            },
                            {
                                label: t('actions.editUserInfo'),
                                variant: "secondaryWithoutIcon",
                                onClick: async () => {
                                    try {
                                        const response = await workflowStepIterator({
                                            access_token: applicationStore.access_token,
                                            constextOfStep: { retry: true, edit_form: true, edit_form_for: NodeTypes.GEOLOCATION_VERIFICATON }
                                        });
                                        applicationStore.setWorkflowNextServiceResponse(response.data);
                                        navigate("/next", { replace: true });
                                    } catch (error) {
                                        toast.error(t('locationVerification.errorOccurred'));
                                    }
                                }
                            }
                        ]
                    });
                    navigate("/error-page", { replace: true });
                }
            }
        };

        processLocation();
    }, [locationStatus, navigate, position, t]);

    return (
        <div className="custom-container">
            <div className="flex justify-between flex-col h-full">
                <div>
                    <div className="text-left">
                        <Button
                            className=""
                            variant="primaryBack"
                            onClick={() => {
                                navigate(`/?aid=${applicationStore.aidCode}`, { replace: true })
                            }}>
                            <LeftArrowWhite />
                        </Button>
                    </div>
                    <div className="grid gap-[16px] mt-10">
                        <div className="text-h5 font-bold text-flow-heading">
                            {locationStatus === "checking" ? t('locationVerification.title') : t('locationVerification.accessing')}
                        </div>
                        <div className="text-body text-Gray900">
                            {locationStatus === "checking" ? t('locationVerification.addressVerifyDescription') : t('locationVerification.verifyingLocationDescription')}
                        </div>
                    </div>
                    <div
                        className="relative mt-10 w-[313px] h-[202px] rounded-[16px] flex justify-center flex-col items-center bg-cover bg-center overflow-hidden"
                        style={{ backgroundImage: `url(${require('../../assets/images/LocationBg.png')})` }}
                    >
                        <div className="absolute inset-0 blur-3xl"></div>
                        {locationStatus === "checking" ? (<figure className="relative w-[160px] h-[160px] mb-6 z-10">
                            <img src={LocationImage} alt={t('common.verification')} />
                        </figure>) : (
                            <Rotator />
                        )
                        }
                    </div>
                    {locationStatus === "checking" && (
                        <div className="mt-6 grid gap-2">
                            <div className="location-container">
                                {t('locationVerification.tapAllow')}
                                <span className='font-bold mx-1'>{t('locationVerification.allow')}</span>
                                {t('locationVerification.browserPromptMessage')}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
})

export default LocationVerification
